import {GROUND_BACKGROUNDS} from '../groundBackgrounds';
import {WIND_GUST_LEAVES} from './windGust';

/**
 * ВУАЛИ ВЕТРА — эффект соляной подложки (salt_1), двух лугов (green_3, green_4)
 * и плато (plato_1). На соли к вуалям добавляются миражи, на лугах — листья.
 *
 * ВУАЛЬ. Ветер срывает с земли пыль и несет ее над полем длинной
 * полупрозрачной вуалью — как прядь шелка или дым на ветру (см.
 * utils/effects/biome/windVeils). Вуаль — пучок прядей; прядь — цепочка
 * коротких штрихов внахлест вдоль своего извилистого русла (так же устроены
 * ленты поземки, snowDrift), а в кадре штриха — четыре тонких волоска. Русла
 * прядей одной вуали — одна общая волна, но вуаль на ней ПЕРЕКРУЧЕНА, как
 * лента: у гребней волны пряди сходятся в узкий яркий жгут («складка»),
 * меняются местами и между гребнями расходятся веером. Так просил владелец
 * игры — по эскизу, где потоки пыли нарисованы пучками тонких линий, которые
 * то расходятся, то пересекаются и собираются снова.
 *
 * Вуаль летит по ветру матча НАД отрядами и их значками, но под тенями
 * облаков, птицами и облаками (слой AIR). Так решил владелец игры, посмотрев
 * вуали в игре: сначала они шли под отрядами, там же, где поземка. Вуаль —
 * тонкие полупрозрачные волоски: отряд под ней читается.
 *
 * СВОЯ ЗЕМЛЯ ВУАЛЕЙ (area, areaFade) — овал на картинке пейзажа (доли ее
 * стороны), в котором вуали видны: к его кромке штрихи тают на последней доле
 * полуосей (areaFade), за ней их нет. У лугов это сам луг, у плато — его
 * вершина: вуаль пыли не висит над пропастью за обрывом и не летит над лесом и
 * морем. У соли своей земли нет: вуали видны везде, где идет их путь.
 *
 * Часть прядей вуали темная, цвета тени: на светлой земле вуаль выдают они,
 * на затененной ячейке и на жетоне — светлые. Соль — самая светлая подложка:
 * белое на белом без темных прядей не видно вовсе.
 *
 * ЧЬИ ЭТО ВУАЛИ. Эффект придуман для соли (вуали соляной пыли) и владельцу
 * игры понравился: по его просьбе те же вуали летят над половиной лугов
 * (green_3, green_4 — над травой, в красках каждого луга; на green_1 и green_2
 * остался порыв ветра с травяными волнами, windGust) и над плато (plato_1 —
 * вместо стада, которое он попросил убрать: вуали пыли над сухой землей, чуть
 * ПЛОЩЕ соляных — линии изогнуты меньше). Звук у вуалей свой у каждой подложки
 * (см. configs/deferredSounds): на соли — «волшебный» ветер, на лугах и плато —
 * шелест ветра в траве.
 *
 * ЛИСТЬЯ — только на лугах. С каждой вуалью летят листья — те же, что с
 * порывом ветра на green_1 и green_2 (краски, размер, качание и трепет — числа
 * порыва, configs/biomeEffects/windGust): владелец игры просил, чтобы на лугах
 * с вуалями листья летели по-прежнему. Лист держится своей вуали: идет ее
 * путем, повторяет ее общую волну, проступает и тает вместе с ней, летит чуть
 * быстрее или медленнее. Листья — над отрядами (слой AIR), как у порыва. Своя
 * земля вуалей (area) им не указ: лист летит по воздуху и над лесом. Подложке
 * без чисел листьев (leaves) листья не положены: на соли и на плато их нет.
 *
 * МИРАЖ — только на соли. Над раскаленной коркой то тут, то там проступает
 * «озеро, которого нет»: плоское широкое пятно цвета бледного неба дрожит, по
 * нему скользят тонкие блики, как по воде, — и тает. Лежит на самой земле: под
 * сеткой ячеек, их дымкой и жетонами. Миражи — фон: главное здесь вуали.
 * Подложке без чисел миражей (mirages) миражи не положены: дорожки миражей у
 * нее нет вовсе.
 *
 * Прежний эффект соли — вспышки бликов кристаллов — владельцу игры не
 * понравился; вуали и миражи пришли ему на смену.
 */
export const WIND_VEILS_KIND = 'windVeils';

// Вуали: числа соляной подложки. У лугов они те же, у плато — свои размах и
// длина волны (см. PLATEAU_VEILS)
const VEILS = Object.freeze({
  // Одна вуаль уже в пути с самого начала (prewarm), следующая — через
  // паузу от начала предыдущей, но разом вуалей не больше двух: третья
  // ждет, пока уйдет первая. Вуаль идет свой путь 9–19 секунд (обычно
  // 12–16), пауза — половина этого срока: на поле почти всегда одна-две
  // вуали, вторая приходит, когда первая на полпути
  firstDelayMs: {min: 3000, max: 6000},
  intervalMs: {min: 5500, max: 8000},
  maxGroups: 2,
  prewarm: 1,
  // Середина пути — там, куда смотрит камера (не дальше стольких размеров
  // ячейки за рамкой поля), в средней части кадра: такая доля его
  // ширины и высоты. По самой кромке кадра вуаль прошла бы наполовину
  // за ним
  fieldMarginFactor: 1,
  viewCoreShare: 0.7,
  // Сколько вуаль проходит (размеров ячейки) и с какой скоростью (в
  // секунду): раза в полтора медленнее поземки — пыль плывет, а не метет
  travelFactor: {min: 20, max: 25},
  speedFactor: {min: 2.1, max: 2.9},
  // В начале и в конце пути вуаль проступает и тает на таком отрезке:
  // появляется она не вся разом, а прядь за прядью
  fadeFactor: 4.5,
  // Длина вуали, размеров ячейки (ячейка в поперечнике — два размера):
  // пять-шесть ячеек вместе с редкими концами
  lengthFactor: {min: 10.5, max: 12},
  // Полуширина веера прядей там, где вуаль расправлена: вся вуаль —
  // около ячейки в ширину
  halfWidthFactor: {min: 0.85, max: 1.25},
  // Общая волна русел: размах и длина, размеров ячейки. На длину вуали
  // приходится одна волна: вуаль ложится широкой буквой S. Короче или
  // размашистее волне быть нельзя: от штриха к штриху прядь поворачивала
  // бы заметным углом
  waveAmplitudeFactor: {min: 0.6, max: 0.95},
  waveLengthFactor: {min: 9.5, max: 13},
});

// Прядь вуали: числа соляной подложки. У лугов они те же, у плато — своя
// мелкая волна пряди (см. PLATEAU_STRAND)
const STRAND = Object.freeze({
  // Прядей в вуали; в кадре штриха пряди четыре волоска: вуаль — две
  // дюжины тонких линий
  count: 6,
  // Сколько из них темных (цвета тени): через равное число прядей
  shadeCount: 2,
  // Прядь собрана из коротких штрихов внахлест: кадр штриха длиннее шага
  // между штрихами во столько раз (края кадра тают плавно, и внахлест
  // вдвое с небольшим плотность вдоль пряди выходит ровной). Штрихов
  // много и они короткие — прядь читается плавной кривой, а не пучком
  // прямых иголок. Потолок спрайтов вуалей: 2 вуали по 6 прядей из 18
  // штрихов — 216
  segmentCount: 18,
  segmentOverlap: 2.2,
  // Где русло идет наискось, штрихи пряди расходятся — кадр штриха там
  // вытягивается следом, чтобы прядь не рассыпалась на «бусины». Но не
  // длиннее стольких размеров ячейки (три четверти ячейки): длинный штрих
  // на крутом повороте русла торчал бы из пряди прямой иглой
  maxSegmentLengthFactor: 1.5,
  // Длина пряди — доля длины вуали; место пряди вдоль вуали случайно: в
  // середине вуали идут все пряди, к обоим концам она редеет до
  // одной-двух. Длиннее прядь не сделать: ее штрихам не осталось бы
  // запаса вытянуться на наклонном русле
  lengthShare: {min: 0.72, max: 0.82},
  // Прядь чуть быстрее или медленнее вуали: пряди скользят друг по другу
  speedShare: {min: 0.95, max: 1.05},
  // Размах общей волны у каждой пряди свой (доля общего)
  waveAmplitudeShare: {min: 0.85, max: 1.15},
  // Складка вуали у каждой пряди сдвинута вдоль волны на столько радиан:
  // пряди сходятся не в одну точку, а жгутом
  foldShiftRad: {min: -0.35, max: 0.35},
  // Своя медленная волна пряди: размах (размеров ячейки) и длина (доля
  // длины общей волны) — пряди не повторяют друг друга
  rippleAmplitudeFactor: {min: 0.06, max: 0.22},
  rippleLengthShare: {min: 1.3, max: 2.1},
  // Высота кадра штриха там, где вуаль расправлена, размеров ячейки: в
  // кадре четыре волоска, каждый — двенадцатая доля его высоты. В
  // складке кадр сжимается до такой доли: волоски пряди сходятся вместе
  // с прядями
  thicknessFactor: {min: 0.45, max: 0.9},
  foldThicknessShare: 0.4,
  // Одни пряди плотнее, другие бледнее (доля плотности краски)
  alphaShare: {min: 0.85, max: 1},
  // Расправленная вуаль бледнее складки: та же пыль лежит шире. Плотность
  // штриха там, где вуаль расправлена, — такая доля плотности в складке
  spreadAlphaShare: 0.8,
  // Плотность вдоль пряди: к обоим концам сходит на нет (степень синуса)
  taperPower: 0.6,
  // Прядь дышит: то плотнее, то бледнее на такую долю
  pulseDepth: 0.14,
  pulsePeriodMs: {min: 2200, max: 4200},
});

/**
 * ВУАЛИ ПЛАТО — ЧУТЬ ПЛОЩЕ соляных (просьба владельца игры: «менее изогнутые
 * линии»). Волна русел ниже и длиннее: размах — около семи десятых соляного,
 * длина — в 1.3 раза больше; веер прядей уже процентов на пятнадцать —
 * складка закручена слабее. Пряди по-прежнему гнутся, сходятся в складку и
 * расходятся, но длинными пологими дугами, а не буквой S: наклон русла к
 * ветру — около трех пятых соляного, отход пряди от прямого пути — около трех
 * четвертей (обе меры сверяет тест эффекта по настоящим позам). На длину
 * вуали теперь приходится не волна, а три четверти волны: складка в вуали
 * одна, реже две. Остальное — как на соли
 */
const PLATEAU_VEILS = Object.freeze({
  ...VEILS,
  halfWidthFactor: {min: 0.72, max: 1.06},
  waveAmplitudeFactor: {min: 0.44, max: 0.68},
  waveLengthFactor: {min: 12.5, max: 17},
});
// Своя волна пряди на плато тоже ниже соляной (и длиннее вместе с общей: ее
// длина — доля длины общей волны)
const PLATEAU_STRAND = Object.freeze({
  ...STRAND,
  rippleAmplitudeFactor: {min: 0.05, max: 0.16},
});

/**
 * ЛИСТЬЯ ВУАЛИ НА ЛУГАХ — листья порыва ветра, но на вуаль их вдвое меньше:
 * порыв идет один (21–27 листьев), вуалей на поле обычно две — листьев в
 * кадре выходит столько же. Потолок спрайтов луга: 2 вуали по 108 штрихов и 13
 * листьев — 242 (у соли с миражами — 243)
 */
const MEADOW_LEAVES = Object.freeze({
  ...WIND_GUST_LEAVES,
  count: {min: 10, max: 13},
  // Листья разбросаны вокруг вуали поперек ее пути в полосе такой ширины,
  // размеров ячейки: чуть шире самой вуали (ее веер — около двух размеров)
  acrossFactor: 3,
});

export const WIND_VEILS_EFFECT = Object.freeze({
  kind: WIND_VEILS_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.SALT_1]: Object.freeze({
      // Вуали пыли
      veils: VEILS,
      // Прядь вуали
      strand: STRAND,
      // Краска и плотность прядей: светлых — теплый белый, темных — тень на
      // соли. Плотность — одного штриха в складке; пряди, сошедшиеся в складке,
      // ее складывают
      light: {tint: 0xfff4e2, alpha: 1},
      shade: {tint: 0x8d7b6a, alpha: 0.4},

      // Миражи
      mirages: {
        // Первый — через несколько секунд после начала, следующий — через
        // паузу от начала предыдущего; разом — не больше трех (обычно один).
        // Потолок спрайтов миражей: 3 миража по 3 пятна и 6 бликов — 27
        firstDelayMs: {min: 3000, max: 8000},
        intervalMs: {min: 8000, max: 14000},
        maxGroups: 3,
        // Мираж живет столько; проступает и тает медленно — за такие доли срока
        durationMs: {min: 11000, max: 16000},
        fadeInShare: 0.25,
        fadeOutShare: 0.3,
        // Место — на корке там, куда смотрит камера (с таким запасом вокруг
        // рамки поля, размеров ячейки), не ближе стольких размеров ячейки к
        // другому миражу. Место ищется столько раз; не нашлось — очередь
        // пропускается
        fieldMarginFactor: 2,
        minGapFactor: 6,
        placeAttempts: 6,
        // Мираж медленно плывет вбок, размеров ячейки в секунду
        driftFactor: {min: 0.04, max: 0.12},
        // «Вода»: несколько плоских линз внахлест — озеро неровной формы
        pool: {
          lobeCount: 3,
          // Кадр средней линзы: ширина (размеров ячейки) и высота (доля
          // ширины) — две-две с половиной ячейки в ширину, вчетверо-впятеро площе
          widthFactor: {min: 4, max: 5.2},
          flatness: {min: 0.2, max: 0.26},
          // Боковые линзы: мельче средней (доля ее размера), сдвинуты от нее
          // вбок (доля ее ширины) и чуть вверх или вниз (доля ее высоты)
          sideScale: {min: 0.55, max: 0.8},
          sideShiftShare: {min: 0.3, max: 0.45},
          sideDropShare: 0.35,
          // Цвет бледного неба; плотность — в разгар миража. Бледнее мираж на
          // корке не разглядеть, плотнее — он уже нарисованное озеро
          tint: 0xa6cdea,
          alpha: 0.28,
          // Дрожь: линза то шире и площе, то уже и выше на такую долю, а
          // плотность ее проседает на такую долю — у каждой линзы в своем темпе
          trembleDepth: 0.07,
          tremblePeriodMs: {min: 900, max: 1700},
          shimmerDepth: 0.3,
          shimmerPeriodMs: {min: 450, max: 900},
        },
        // Блики: тонкие горизонтальные штрихи вспыхивают, скользят и гаснут
        // на средней линзе — каждый раз на новом месте
        glitter: {
          count: 6,
          // Длина (в разгар вспышки) и высота кадра штриха, размеров ячейки
          lengthFactor: {min: 0.45, max: 1},
          thicknessFactor: {min: 0.09, max: 0.14},
          // Одна вспышка длится столько; за это время блик проходит столько
          // размеров ячейки — вправо или влево
          cycleMs: {min: 700, max: 1500},
          slideFactor: {min: 0.15, max: 0.4},
          // Вспышки держатся середины линзы: не дальше такой доли полуосей ее
          // кадра от середины
          reachShare: 0.42,
          tint: 0xf6fbff,
          alpha: 0.9,
        },
      },
      // Соляная корка на картинке пейзажа (доли ее стороны): миражи — только
      // на ней, и не на самой кромке — не на последней доле ее полуосей
      pan: {u: 0.5, v: 0.5, radiusU: 0.33, radiusV: 0.31},
      panFade: 0.1,
    }),

    /**
     * ЛУГА И ПЛАТО: те же вуали, без миражей; на лугах — с листьями. Краски прядей у каждой подложки
     * свои и сняты с ее картинки (sharp; середина картинки, доли стороны
     * 0.3–0.7 по обеим осям, — земля под полем; точки разобраны по яркости):
     * светлая прядь — самая светлая земля подложки (верхние 2 % точек),
     * высветленная до бледного тона; темная — самая темная (нижние 10 %),
     * притемненная вдвое с лишним: темнее всего, что под ней лежит.
     *
     * На траве и на сухой земле плато светлая прядь заметнее, чем на соли
     * (там белое лежит на почти белом), поэтому плотность ее ниже соляной:
     * вуаль читается так же — тонкими линиями, а не белыми царапинами.
     *
     * Своя земля (area) — с запасом вокруг поля: все ячейки поля лежат там,
     * где вуаль видна в полную силу, таять она начинает за ними
     */
    // Золотая степь. Трава под полем: от 0x5f5a2b (тень) до 0xb29e5b (на
    // свету), в среднем 0x887b3d
    [GROUND_BACKGROUNDS.GREEN_3]: Object.freeze({
      veils: VEILS,
      strand: STRAND,
      light: {tint: 0xfaeab8, alpha: 0.8},
      shade: {tint: 0x2b2813, alpha: 0.45},
      leaves: MEADOW_LEAVES,
      // Луг: дальше — леса, озера и море
      area: {u: 0.49, v: 0.5, radiusU: 0.33, radiusV: 0.33},
      areaFade: 0.2,
    }),
    // Речная равнина, выгоревшая до соломенной. Трава под полем: от 0x776d38
    // до 0xbea667, в среднем 0x96864c — светлее и теплее степи
    [GROUND_BACKGROUNDS.GREEN_4]: Object.freeze({
      veils: VEILS,
      strand: STRAND,
      light: {tint: 0xfae8bc, alpha: 0.8},
      shade: {tint: 0x363119, alpha: 0.45},
      leaves: MEADOW_LEAVES,
      // Равнина между горами, лесом и рекой
      area: {u: 0.5, v: 0.5, radiusU: 0.33, radiusV: 0.33},
      areaFade: 0.2,
    }),
    // Плато: сухая рыжая земля с редкой травой. Под полем: от 0x665535 до
    // 0x9e866b, в среднем 0x866d4f. Светлые пряди — бледная пыль, темные —
    // бурая тень
    [GROUND_BACKGROUNDS.PLATO_1]: Object.freeze({
      veils: PLATEAU_VEILS,
      strand: PLATEAU_STRAND,
      light: {tint: 0xf6dfc4, alpha: 0.7},
      shade: {tint: 0x2e2618, alpha: 0.45},
      // Вершина плато: дальше — обрыв и лес в ущелье
      area: {u: 0.48, v: 0.49, radiusU: 0.32, radiusV: 0.3},
      areaFade: 0.15,
    }),
  }),
});
